import type {IChildPartner} from '../../../../dto/Company.dto';

/**
 * The district fields the TNN campaign endpoints attach to a partner. Kept
 * structural so it fits ICompany, IPartnerCompany and the amount table's row
 * type alike.
 */
export type IDistrictChildFields = {
  child_companies?: IChildPartner[];
  inherited_children?: IChildPartner[];
};

export type IDistrictPartner = IDistrictChildFields & {
  id: number;
  name: string;
};

/**
 * Maps a child partner id to the selected district partner that already covers
 * it. A district shares its TNN campaign with every active child on the same
 * product, so a covered child must not be selected on its own as well.
 */
export type IDistrictCoverage<T extends IDistrictPartner> = Map<number, T>;

/**
 * Every child partner belonging to this district on the product being picked.
 * Used to decide what a district selection covers.
 */
export const getDistrictChildren = (
  company: IDistrictChildFields,
): IChildPartner[] =>
  company.child_companies ?? company.inherited_children ?? [];

/**
 * The children that actually inherit a saved campaign through this district.
 * Falls back to the full membership while the campaign is still being created.
 */
export const getInheritedChildren = (
  company: IDistrictChildFields,
): IChildPartner[] =>
  company.inherited_children ?? company.child_companies ?? [];

export const buildDistrictCoverage = <T extends IDistrictPartner>(
  selectedCompanies: T[],
): IDistrictCoverage<T> => {
  const coverage: IDistrictCoverage<T> = new Map();

  selectedCompanies.forEach(district => {
    getDistrictChildren(district).forEach(child => {
      if (child.id !== district.id && !coverage.has(child.id)) {
        coverage.set(child.id, district);
      }
    });
  });

  return coverage;
};

/**
 * Selected partners that a district in the same selection already covers,
 * grouped by that district so each warning can name it.
 */
export const groupCoveredCompaniesByDistrict = <T extends IDistrictPartner>(
  selectedCompanies: T[],
  coverage: IDistrictCoverage<T>,
): {district: T; children: T[]}[] => {
  const grouped: {district: T; children: T[]}[] = [];

  selectedCompanies.forEach(company => {
    const district = coverage.get(company.id);
    if (!district) {
      return;
    }

    const group = grouped.find(entry => entry.district.id === district.id);
    if (group) {
      group.children.push(company);
    } else {
      grouped.push({district, children: [company]});
    }
  });

  return grouped;
};
